<template>
  <header class="admin-header">
    <div class="header-left">
      <button type="button" class="icon-button" aria-label="Afficher ou masquer le menu" @click="$emit('toggle')">
        <i class="fas fa-bars" aria-hidden="true"></i>
      </button>
      <div class="header-title">
        <i v-if="route.meta.icon" :class="route.meta.icon" aria-hidden="true"></i>
        <span>{{ route.meta.title }}</span>
      </div>
    </div>

    <div class="header-right">
      <router-link v-if="licenseWarning" to="/licence" class="license-pill" :class="licenseWarning.tone">
        <i class="fas fa-key" aria-hidden="true"></i>
        <span>{{ licenseWarning.text }}</span>
      </router-link>

      <div ref="notificationsRef" class="menu">
        <button type="button" class="icon-button" :aria-expanded="openMenu === 'notifications'" aria-haspopup="menu" :aria-label="`Notifications (${unreadCount || 0} non lue${unreadCount > 1 ? 's' : ''})`" @click="toggle('notifications')">
          <i class="fas fa-bell" aria-hidden="true"></i>
          <span v-if="unreadCount" class="badge-dot" aria-hidden="true">{{ unreadBadge }}</span>
        </button>
        <div v-if="openMenu === 'notifications'" class="menu-panel menu-panel--wide" role="menu">
          <div class="menu-head">
            <strong>Notifications<template v-if="unreadCount"> ({{ unreadBadge }})</template></strong>
            <button type="button" class="btn btn-link btn-sm p-0" :disabled="!unreadCount" @click="readAll"><i class="fas fa-check-double" aria-hidden="true"></i>Tout marquer comme lu</button>
          </div>
          <p v-if="!notify.length" class="menu-empty">Aucune notification.</p>
          <button
            v-for="notification in notify.slice(0, 8)"
            :key="notification.id"
            type="button"
            class="notification-item"
            :class="{ unread: !notification.read_at }"
            role="menuitem"
            @click="openNotification(notification)"
          >
            <i :class="isSignatureNotification(notification) ? 'fas fa-signature' : 'fas fa-file-signature'" aria-hidden="true"></i>
            <span>
              <span class="notification-text">{{ notification.data?.message || notification.message }}</span>
              <small :title="formatFull(notification.created_at)">{{ formatRelative(notification.created_at) }}</small>
            </span>
          </button>
          <router-link :to="{ name: 'notifications' }" class="menu-footer-link" role="menuitem" @click="openMenu = null">
            <i class="fas fa-list" aria-hidden="true"></i>Voir toutes les notifications
          </router-link>
        </div>
      </div>

      <div ref="userRef" class="menu">
        <button type="button" class="user-button" :aria-expanded="openMenu === 'user'" aria-haspopup="menu" @click="toggle('user')">
          <UserAvatar :src="user?.photo" :name="user?.nom_complet" :size="34" />
          <span class="user-meta">
            <strong>{{ user?.nom_complet }}</strong>
            <small>{{ user?.role_libelle || user?.role }}</small>
          </span>
          <i class="fas fa-chevron-down small" aria-hidden="true"></i>
        </button>
        <div v-if="openMenu === 'user'" class="menu-panel" role="menu">
          <div class="menu-head menu-head--user">
            <strong>{{ user?.nom_complet }}</strong>
            <small>{{ user?.email }}</small>
          </div>
          <router-link to="/profil" class="menu-item" role="menuitem" @click="openMenu = null"><i class="fas fa-user" aria-hidden="true"></i>Mon profil</router-link>
          <router-link to="/aide" class="menu-item" role="menuitem" @click="openMenu = null"><i class="fas fa-circle-question" aria-hidden="true"></i>Aide et guide</router-link>
          <router-link to="/change-password" class="menu-item" role="menuitem" @click="openMenu = null"><i class="fas fa-lock" aria-hidden="true"></i>Changer mon mot de passe</router-link>
          <button type="button" class="menu-item text-danger" role="menuitem" :disabled="loggingOut" @click="logout">
            <i class="fas fa-right-from-bracket" aria-hidden="true"></i>{{ loggingOut ? "Déconnexion…" : "Se déconnecter" }}
          </button>
        </div>
      </div>
    </div>
  </header>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useStore } from "vuex";
import http from "@/api/http.js";
import UserAvatar from "@/components/ui/UserAvatar.vue";
import { useNotifications } from "@/composables/useNotification.js";
import { usePermissions } from "@/helpers/permissions";

defineProps({ collapsed: { type: Boolean, default: false } });
defineEmits(["toggle"]);

const route = useRoute();
const router = useRouter();
const store = useStore();
const { can } = usePermissions();

const user = computed(() => store.state.auth.user);
const userId = computed(() => store.state.auth.user?.id);
const { notify, unreadCount, refreshNotifications } = useNotifications(userId);

const openMenu = ref(null);
const notificationsRef = ref(null);
const userRef = ref(null);
const loggingOut = ref(false);

const toggle = (name) => (openMenu.value = openMenu.value === name ? null : name);

const closeOnOutside = (event) => {
  if (![notificationsRef.value, userRef.value].some((element) => element?.contains(event.target))) {
    openMenu.value = null;
  }
};
const closeOnEscape = (event) => event.key === "Escape" && (openMenu.value = null);

onMounted(() => {
  document.addEventListener("click", closeOnOutside);
  document.addEventListener("keydown", closeOnEscape);
  if (canSeeLicense.value) store.dispatch("license/fetchCurrentLicense");
});
onBeforeUnmount(() => {
  document.removeEventListener("click", closeOnOutside);
  document.removeEventListener("keydown", closeOnEscape);
});

/** Licence affichée dans l'en-tête : permission license.status (ou license.view). */
const canSeeLicense = computed(() => can("license.status") || can("license.view"));

const licenseWarning = computed(() => {
  if (!canSeeLicense.value) return null;
  const license = store.state.license.currentLicense;
  if (!license) return null;
  if (license.status === "aucune") return { tone: "danger", text: "Aucune licence" };
  if (license.status === "expired") return { tone: "danger", text: "Licence expirée" };
  const jours = Number(license.jours) || 0;
  const text = `Licence : ${jours} j restant${jours > 1 ? "s" : ""}`;
  if (jours <= 15) return { tone: "warning", text };
  return { tone: "success", text };
});

const unreadBadge = computed(() => (unreadCount.value > 99 ? "99+" : String(unreadCount.value)));

const formatFull = (value) => (value ? new Date(value).toLocaleString("fr-FR", { dateStyle: "full", timeStyle: "short" }) : "");

const formatRelative = (value) => {
  if (!value) return "";
  const minutes = Math.round((Date.now() - new Date(value).getTime()) / 60000);
  if (minutes < 1) return "à l'instant";
  if (minutes < 60) return `il y a ${minutes} min`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `il y a ${hours} h`;
  return new Date(value).toLocaleDateString("fr-FR");
};

/**
 * Données d'une notification : sous « data » (API) ou à plat (diffusion temps réel).
 */
const notificationData = (notification) => notification.data || notification;

/**
 * Notification liée à une demande de signature des bulletins.
 */
const isSignatureNotification = (notification) => {
  const data = notificationData(notification);
  return data.type === "signature_bulletin" || Boolean(data.signature_bulletin_id);
};

const openNotification = async (notification) => {
  openMenu.value = null;
  if (!notification.read_at && notification.id) {
    await http.post(`notifications/${notification.id}/read`).catch(() => null);
    await refreshNotifications();
  }
  const data = notificationData(notification);
  if (isSignatureNotification(notification)) {
    router.push({ name: "signaturesBulletins", query: { demande: data.signature_bulletin_id } });
  } else if (data.demande_id) {
    router.push({ name: "demandes", query: { demande: data.demande_id } });
  }
};

const readAll = async () => {
  await http.post("notifications/read-all").catch(() => null);
  await refreshNotifications();
};

const logout = async () => {
  loggingOut.value = true;
  await store.dispatch("auth/logout");
  store.commit("license/clearLicense");
  loggingOut.value = false;
  router.replace({ name: "login" });
};
</script>

<style scoped>
.admin-header {
  position: sticky;
  top: 0;
  z-index: 1030;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0 1.25rem;
  background: hsl(var(--card) / 0.92);
  backdrop-filter: blur(8px);
  border-bottom: 1px solid hsl(var(--border));
}
.header-left,
.header-right {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  min-width: 0;
}
.header-title {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.header-title i {
  color: hsl(var(--primary));
}
.icon-button {
  position: relative;
  width: 38px;
  height: 38px;
  display: grid;
  place-items: center;
  border: 1px solid hsl(var(--border));
  border-radius: var(--radius);
  background: hsl(var(--card));
  color: hsl(var(--foreground));
}
.icon-button:hover {
  background: hsl(var(--accent));
}
.badge-dot {
  position: absolute;
  top: -6px;
  right: -6px;
  min-width: 18px;
  height: 18px;
  padding: 0 4px;
  border-radius: 999px;
  background: hsl(var(--destructive));
  color: #fff;
  font-size: 0.65rem;
  font-weight: 700;
  line-height: 18px;
}
.license-pill {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.3rem 0.7rem;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 600;
  text-decoration: none;
}
.license-pill.success { background: hsl(var(--primary) / 0.12); color: hsl(var(--primary)); }
.license-pill.warning { background: hsl(var(--warning) / 0.18); color: hsl(30 80% 30%); }
.license-pill.danger { background: hsl(var(--destructive) / 0.12); color: hsl(var(--destructive)); }
.menu {
  position: relative;
}
.user-button {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  border: 0;
  background: transparent;
  padding: 0.25rem 0.4rem;
  border-radius: var(--radius);
  text-align: left;
}
.user-button:hover {
  background: hsl(var(--accent));
}
.user-meta {
  display: flex;
  flex-direction: column;
  line-height: 1.15;
}
.user-meta strong {
  font-size: 0.85rem;
}
.user-meta small {
  color: hsl(var(--muted-foreground));
  font-size: 0.72rem;
}
.menu-panel {
  position: absolute;
  right: 0;
  top: calc(100% + 8px);
  width: 260px;
  padding: 0.4rem;
  background: hsl(var(--popover));
  border: 1px solid hsl(var(--border));
  border-radius: calc(var(--radius) + 2px);
  box-shadow: 0 16px 36px rgb(0 0 0 / 0.12);
  text-align: left;
}
.menu-panel--wide {
  width: 340px;
}
.menu-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0.6rem;
  border-bottom: 1px solid hsl(var(--border));
  margin-bottom: 0.3rem;
}
.menu-head--user {
  flex-direction: column;
  align-items: flex-start;
}
.menu-head--user small {
  color: hsl(var(--muted-foreground));
}
.menu-empty {
  margin: 0;
  padding: 1rem;
  text-align: center;
  color: hsl(var(--muted-foreground));
  font-size: 0.85rem;
}
.menu-item,
.notification-item {
  display: flex;
  align-items: flex-start;
  gap: 0.6rem;
  width: 100%;
  padding: 0.55rem 0.6rem;
  border: 0;
  border-radius: var(--radius);
  background: transparent;
  color: hsl(var(--foreground));
  font-size: 0.85rem;
  text-align: left;
  text-decoration: none;
}
.menu-item:hover,
.notification-item:hover {
  background: hsl(var(--accent));
}
.menu-item i,
.notification-item i {
  width: 1rem;
  margin-top: 0.15rem;
  color: hsl(var(--muted-foreground));
}
.menu-footer-link {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.4rem;
  margin-top: 0.3rem;
  padding: 0.55rem 0.6rem;
  border-top: 1px solid hsl(var(--border));
  color: hsl(var(--primary));
  font-size: 0.82rem;
  font-weight: 600;
  text-decoration: none;
}
.menu-footer-link:hover {
  background: hsl(var(--accent));
  border-radius: var(--radius);
}
.notification-item.unread {
  background: hsl(var(--primary) / 0.06);
}
.notification-item small {
  display: block;
  color: hsl(var(--muted-foreground));
  font-size: 0.72rem;
}
@media (max-width: 767px) {
  .user-meta,
  .license-pill span {
    display: none;
  }
  .admin-header {
    padding: 0 0.75rem;
  }
}
</style>
